/*
 * @providesModule SKZNotification
 * @format
 */

import SKZTrophy from 'SKZTrophy';
import _ from 'lodash';
import {Platform} from 'react-native';
import CleverTap from 'clevertap-react-native';

export class SKZNotificationRecord {
  id: string | undefined;
  title: string | undefined;
  message: string;
  action: string;
  tournamentId: string | undefined;
  deepLinkUrl: string | undefined;
  trophyId: string | undefined;
  fromSkillzSDKLaunch: boolean;
}

export const NotificationTypes = {
  REMATCH: '1',
  REGEN_AVAILABLE: '2', // deprecated in Majora's Mask
  CHALLENGE_DECLINED: '3',
  TOURNAMENT_UNLOCKED: '4',
  LEADERBOARD_PRIZE: '5',
  LEADERBOARD_NO_PRIZE: '6',
  LEADERBOARD_ENDS_SOON: '7',
  TROPHY_AWARDED_IN_APP: '8',
  LEADERBOARD_START_ALERT: '9',
  LEADERBOARD_RESULTS: 'LB',
  DISPLAY_LINK: 'LK',
  TROPHY_AWARDED: 'TR',
  TOURNAMENT_RESULTS: 'tournamentResults',
  IN_APP_TOAST: 'IN-APP',
  CHAT: Platform.select({
    ios: 'CHAT',
    android: 'showChat',
  }),
  PLAYER_LEVEL: 'PLAYER_LEVEL',
  NOTIFY_TURN_ACTION: 'NT',
  TROPHY_EARNED: 'trophyEarned',
  LOCAL_LIVE_EVENT: 'LE',
};

export const NotificationIconImages = {
  BRACKET: 'ic_notification_bracket_skz.png',
  GENERIC: 'ic_notification_generic_skz.png',
  LEADERBOARD: 'ic_notification_leaderboard_skz.png',
  LIVE_EVENT: 'ic_notification_live_event_skz.png',
  MATCH_RESULT: 'ic_notification_match_result_skz.png',
  REMATCH: 'ic_notification_rematch_skz.png',
  TIE_BREAKER: 'ic_notification_tie_breaker_skz.png',
  TROPHY: 'ic_notification_trophy_skz.png',
  TURN_BASED: 'ic_notification_turn_based_skz.png',
};

export interface GeneralToast {
  message: string;
  subtext: string;
  iconResource: string;
  action: object;
  params: object;
}

export interface ChatToast {
  fromContact: string | undefined;
  messageBody: string;
  params: object;
}

export interface TrophyToast {
  trophy: SKZTrophy;
}

export type Toast = ChatToast | TrophyToast | GeneralToast;

export default class SKZNotification extends SKZNotificationRecord {
  static createNotificationFromNative(notifString: string): SKZNotification {
    let data: Object = {};
    try {
      data = JSON.parse(notifString);
    } catch (e) {}

    if (Platform.OS == 'ios') {
      return SKZNotification.createNotificationFromNativeiOS(data);
    } else {
      return SKZNotification.createNotificationFromNativeAndroid(data);
    }
  }

  static createNotificationFromNativeiOS(data: Object): SKZNotification {
    const actionData: string = _.get(data, 'action');
    const deepLinkData: string | null = _.get(data, 'deepLinkUrl') ?? _.get(data, 'wzrk_dl');
    const trophyIdData: string | null = _.get(data, 'trophyId');
    const tournamentIdData: string | null = _.get(data, 'tournamentId');
    const fromLaunch: boolean = _.get(data, 'fromSkillzSDKLaunch', false);

    const notificationObj: Object = {
      action: actionData,
      deepLinkUrl: deepLinkData,
      tournamentId: tournamentIdData,
      trophyId: trophyIdData,
      fromSkillzSDKLaunch: fromLaunch,
    };

    const messageData: string | object = _.get(data, 'aps.alert');

    if (typeof messageData == 'object') {
      const titleData: string | null = _.get(data, 'aps.alert.title');
      const subtitleData: string | null = _.get(data, 'aps.alert.body');

      _.set(notificationObj, 'title', titleData);
      _.set(notificationObj, 'message', subtitleData);
    } else {
      _.set(notificationObj, 'message', messageData);
    }

    return new SKZNotification(notificationObj);
  }

  static createNotificationFromNativeAndroid(data: Object): SKZNotification {
    // TODO: verify for chat messages, trophy, and match results the additional parameters
    const notificationObj = {
      id: _.get(data, 'id'),
      title: _.get(data, 'title'),
      message: _.get(data, 'message'),
      action: _.get(data, 'action'),
      deepLinkUrl: _.get(data, 'deepLinkUrl'),
      fromSkillzSDKLaunch: _.get(data, 'fromSkillzSDKLaunch', false),
    };
    return new SKZNotification(notificationObj);
  }

  constructor(keyValueSource: Object) {
    super();
    return _.create(SKZNotification.prototype, keyValueSource);
  }

  static make(notifications: Array<SKZNotificationRecord>): Array<SKZNotification> {
    if (Array.isArray(notifications)) {
      return _.map(
        notifications,
        (notificationRecord: SKZNotificationRecord): SKZNotification =>
          new SKZNotification(notificationRecord)
      );
    }
    return [];
  }

  isFromSDKLaunch(): boolean {
    return this.fromSkillzSDKLaunch;
  }

  getAction(): string {
    return this.action;
  }

  getDeepLink(): string | undefined {
    return this.deepLinkUrl;
  }

  getMessageBody(): string {
    return this.message;
  }

  getMessageTitle(): string | undefined {
    return this.title;
  }

  getTournamentId(): string | undefined {
    return this.tournamentId;
  }

  getTrophyId(): string | undefined {
    return this.trophyId;
  }

  getNotificationId(): string | undefined {
    return this.id;
  }

  getChatRoomName(): string | null {
    if (this.deepLinkUrl?.length > 0) {
      const lowerCase = this.deepLinkUrl?.toLowerCase();
      const chatLink = 'skillz://showChat?roomName='.toLowerCase();
      if (Platform.OS == 'ios' && _.startsWith(lowerCase, chatLink)) {
        const deepLinkArray: string[] = _.split(this.deepLinkUrl, '=');
        return deepLinkArray[1];
      } else if (Platform.OS == 'android') {
        const split: string[] = _.split(this.deepLinkUrl, '/?');
        const params: string[] = _.split(split[split.length - 1], '&');
        for (const singleParams of params) {
          if (_.startsWith(singleParams, 'roomName=')) {
            const nameVal: string[] = _.split(singleParams, '=');
            return nameVal[1];
          }
        }
      }
    }
    return null;
  }

  getChatToastFromNotif(): ChatToast {
    const params = {};
    const chatRoomName: string | null = this.getChatRoomName();

    if (chatRoomName != null && chatRoomName.length > 0) {
      _.set(params, 'chatRoomName', chatRoomName);
    }
    const toast: ChatToast = {
      fromContact: '',
      messageBody: '',
      params: params,
    };
    const notifDataMessage = this.getMessageBody();

    if (notifDataMessage?.length > 0) {
      const split: string[] = _.split(notifDataMessage, ': ');
      if (split.length > 1) {
        _.set(toast, 'fromContact', split[0]);
        const messageBody: string[] = _.slice(split, 1, split.length);
        _.set(toast, 'messageBody', _.join(messageBody, ': '));

        console.debug(
          'PushNotificationController: parsing chat notif, sender: ' + toast.fromContact
        );
        console.debug('PushNotificationController: parsing chat notif, body: ' + toast.messageBody);
      }
    }

    return toast;
  }

  isActionEqualToType(type: string): boolean {
    return this.action == type;
  }

  isNotificationIdEqualToType(type: string): boolean {
    return this.id == type;
  }

  isLeaderboardNotification(): boolean {
    return (
      this.isNotificationIdEqualToType(NotificationTypes.LEADERBOARD_PRIZE) ||
      this.isNotificationIdEqualToType(NotificationTypes.LEADERBOARD_NO_PRIZE) ||
      this.isNotificationIdEqualToType(NotificationTypes.LEADERBOARD_ENDS_SOON) ||
      this.isNotificationIdEqualToType(NotificationTypes.LEADERBOARD_START_ALERT) ||
      this.isNotificationIdEqualToType(NotificationTypes.TOURNAMENT_UNLOCKED)
    );
  }
}
